import { Link, useLocation } from "react-router-dom"
import { parseCommsChannel, type CommsChannel } from "@/lib/commsChannel"

interface Tab {
  label: string
  to: string
  pathname: string
  // `null` = the tab matches only when there is no (valid) channel param.
  channel: CommsChannel | null
}

const TABS: Tab[] = [
  { label: "Triage", to: "/comms", pathname: "/comms", channel: null },
  { label: "Events", to: "/comms/events", pathname: "/comms/events", channel: null },
  {
    label: "Support",
    to: "/comms/threads?channel=support",
    pathname: "/comms/threads",
    channel: "support",
  },
  {
    label: "Legal",
    to: "/comms/threads?channel=legal",
    pathname: "/comms/threads",
    channel: "legal",
  },
  {
    label: "Voicemail",
    to: "/comms/threads?channel=voicemail",
    pathname: "/comms/threads",
    channel: "voicemail",
  },
  {
    label: "Primary",
    to: "/comms/threads?channel=primary",
    pathname: "/comms/threads",
    channel: "primary",
  },
  { label: "All threads", to: "/comms/threads", pathname: "/comms/threads", channel: null },
  {
    label: "Crop waitlist",
    to: "/comms/crop-waitlist",
    pathname: "/comms/crop-waitlist",
    channel: null,
  },
  {
    label: "Cheer list",
    to: "/comms/cheer-waitlist",
    pathname: "/comms/cheer-waitlist",
    channel: null,
  },
]

/** Top-of-page navigation shared by Triage and the thread lists. */
export function CommsTabs() {
  const { pathname, search } = useLocation()
  const channel = parseCommsChannel(new URLSearchParams(search).get("channel"))
  const path = pathname.replace(/\/+$/, "") || "/"

  return (
    <nav
      aria-label="Comms views"
      className="inline-flex max-w-full flex-wrap rounded-lg border border-border bg-surface p-0.5 text-sm"
    >
      {TABS.map((tab) => {
        const active = tab.pathname === path && tab.channel === channel
        return (
          <Link
            key={tab.label}
            to={tab.to}
            aria-current={active ? "page" : undefined}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              active ? "bg-primary text-contrast" : "text-muted-foreground hover:text-primary"
            }`}
          >
            {tab.label}
          </Link>
        )
      })}
    </nav>
  )
}

/** Channel pill: support / legal / voicemail. Primary threads render nothing unless `showPrimary`. */
export function ChannelPill({
  channel,
  isLegal,
  showPrimary = false,
}: {
  channel: "support" | "primary" | "voicemail"
  isLegal: boolean
  showPrimary?: boolean
}) {
  const pill = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
  if (channel === "support" && isLegal)
    return <span className={`${pill} bg-rose-100 text-rose-800`}>legal</span>
  if (channel === "support") return <span className={`${pill} bg-amber-100 text-amber-800`}>support</span>
  if (channel === "voicemail")
    return <span className={`${pill} bg-violet-100 text-violet-800`}>voicemail</span>
  return showPrimary ? <span className="text-xs text-muted-foreground">primary</span> : null
}
